import {
  CtaClick,
  InteractivesScene,
  SceneProgressionPoint,
} from "../Analytics";
import { ComputedDatum, ResponsiveBar } from "@nivo/bar";
import { theme } from "../../../theme";
import { TooltipFunnel } from "./Tooltips";
type Scene = {
  name: string;
  changes: number;
  interactive?: CtaClick[];
  sessionsClickedAnyInteractive?: number;
};
export type EventType = "player" | "scene" | "interactive";
const numberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
});
export const Funnel = ({
  data,
}: {
  data: {
    ready?: number;
    firstPlay?: number;
    scenesProgression?: Array<SceneProgressionPoint>;
    interactives?: Array<InteractivesScene>;
  };
}) => {
  if (!data.ready && !data.firstPlay && !data.scenesProgression?.length)
    return null;

  const scenes: { [s: string]: Scene } = {};
  data.scenesProgression?.forEach((row) => {
    scenes[row.sceneId] = {
      name: row.sceneId,
      changes: row.uniqueSessions,
    };
  });
  if (data.interactives?.length) {
    data.interactives.forEach((scene) => {
      scenes[scene.sceneId] = scenes[scene.sceneId] || {
        name: scene.sceneId,
        changes: 0,
      };
      scenes[scene.sceneId].interactive = scene.ctas;
      scenes[scene.sceneId].sessionsClickedAnyInteractive = scene.uniqueSessions;
    });
  }
  const totalSessions = data.ready || 0;
  const totalFirstPlay = data.firstPlay || 0;
  const funnelData: Array<{
    key: string;
    events: number;
    type: EventType;
    percentage: number;
    ctaDetail?: string;
  }> = [
    {
      key: "First Play",
      events: totalFirstPlay,
      type: "player",
      percentage:
        totalSessions > 0
          ? parseFloat(((totalFirstPlay * 100) / totalSessions).toFixed(1))
          : 0,
    },
  ];

  Object.values(scenes)
    .sort((a, b) => b.changes - a.changes)
    .forEach((scene) => {
      if (scene.name === "no-scene") return;
      const cappedValue = Math.min(scene.changes, totalFirstPlay);
      funnelData.push({
        key: scene.name,
        events: cappedValue || 0,
        type: "scene",
        percentage:
          totalSessions > 0
            ? parseFloat(((cappedValue * 100) / totalSessions).toFixed(1))
            : 0,
      });
      if (scene.interactive) {
        const sessionsClickedAnyInteractive = Math.min(
          scene.sessionsClickedAnyInteractive || 0,
          totalSessions
        );
        const bar = {
          key: `${scene.name} interactives`,
          type: "interactive" as EventType,
          events: sessionsClickedAnyInteractive,
          percentage:
            totalSessions > 0
              ? parseFloat(
                  (
                    (sessionsClickedAnyInteractive * 100) /
                    totalSessions
                  ).toFixed(1)
                )
              : 0,
          ctaDetail: JSON.stringify(scene.interactive),
        };

        funnelData.push(bar);
      }
    });
  const colors: Record<EventType, string> = {
    player: theme.blings_teal_60,
    scene: theme.blings_teal,
    interactive: theme.primary,
  };

  return (
    <div className="analytics-view funnel" style={{ height: "fit-content" }}>
      <div className="view-title">
        <h2>Funnel</h2>
        <h2>
          Sessions{" "}
          <span>{(data.ready ?? 0).toLocaleString("en-US")}</span>
        </h2>
      </div>
      <div
        style={{ width: "100%", height: "350px" }}
        className="funnel-responsive-bar"
      >
        <ResponsiveBar
          data={funnelData}
          keys={["events"]}
          indexBy="key"
          layers={[
            "grid",
            "axes",
            "bars",
            "markers",
            "legends",
            "annotations",
            ({ bars }) => {
              return (
                <g>
                  {bars.map(({ width, height, x, data }) => {
                    const translateX = x + width / 2;
                    const translateY = -height - 40;
                    return (
                      <text
                        key={data.data.key}
                        style={{
                          position: "absolute",
                          transform: `translateX(${translateX}px) translateY(80%) translateY(${translateY}px)`,
                          fontSize: "10px",
                        }}
                        textAnchor="middle"
                        dominantBaseline="central"
                      >
                        {`${
                          data.data.percentage ? data.data.percentage + "%" : ""
                        }`}
                      </text>
                    );
                  })}
                </g>
              );
            },
          ]}
          colors={(
            d: ComputedDatum<{
              key: string;
              events: number;
              type: EventType;
              percentage: number;
              ctaDetail?: string;
            }>
          ) => colors[d.data.type]}
          theme={{
            background: "#F7F8FA",
            axis: { domain: { line: { stroke: "gray", strokeWidth: 1 } } },
            grid: { line: { stroke: theme.blings_teal_80, strokeWidth: 0.1 } },
          }}
          padding={0.5}
          axisLeft={{
            format: (l) => numberFormatter.format(l),
          }}
          axisBottom={{
            tickRotation: -25,
            format: (v) => {
              return v.length > 18 ? (
                <tspan>
                  {"..." + v.substr(-17)}
                  <title>{v}</title>
                </tspan>
              ) : (
                v
              );
            },
          }}
          margin={{
            left: 50,
            right: 30,
            top: 30,
            bottom: 70,
          }}
          tooltip={TooltipFunnel}
          valueFormat={() => ""}
          legends={[
            {
              dataFrom: "indexes",
              data: [
                {
                  id: "scenes",
                  label: "Scenes",
                  color: colors.scene,
                },
                {
                  id: "interactivity",
                  label: "Interactivity",
                  color: colors.interactive,
                },
              ],
              anchor: "top-right",
              direction: "column",
              itemHeight: 20,
              itemWidth: 80,
              translateY: 0,
            },
          ]}
        />
      </div>
    </div>
  );
};
